import { useCallback, useEffect, useState } from "react";
import { getSettings, updateSettings, type Settings, type SettingsUpdate } from "../services/api";
import { useLang } from "../hooks/useLang";
import { useAnswerLang } from "../hooks/useAnswerLang";
import LanguageSelect from "./LanguageSelect";
import Icon from "./icons";
import CreditsPanel from "./CreditsPanel";
import StaffPanel from "./StaffPanel";
import BillingPanel from "./BillingPanel";
import ProjectsPanel from "./ProjectsPanel";

export default function SettingsPanel() {
  const { t } = useLang();
  const { lang: answerLang, select: selectAnswerLang } = useAnswerLang();
  const [s, setS] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  // Yerel düzenleme buffer (kullanıcı kaydete basana kadar)
  const [draft, setDraft] = useState<SettingsUpdate>({});

  // Tab navigation (Personel → Yönetim sayfasına taşındı)
  // AI sağlayıcı seçimi kaldırıldı — sağlayıcı admin panelinden global belirlenir,
  // tüm kullanıcılar onu kullanır (müşteri paneli AI seçmez).
  // Yönetim (Management) sayfası buraya birleştirildi: kredi/personel/fatura/projeler
  // tab'ları da Ayarlar altında.
  type TabKey = "general" | "credits" | "staff" | "billing" | "projects" | "language";
  const [tab, setTab] = useState<TabKey>(() => {
    const saved = localStorage.getItem("settings_tab") as TabKey;
    return ["general", "credits", "staff", "billing", "projects", "language"].includes(saved) ? saved : "general";
  });
  const switchTab = (k: TabKey) => { setTab(k); localStorage.setItem("settings_tab", k); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cur = await getSettings();
      setS(cur);
      setDraft({
        org_name: cur.org_name,
        logo_data_url: cur.logo_data_url,
        docx_footer: cur.docx_footer,
        // default_ai_provider BİLİNÇLİ OLARAK YOK. AI sağlayıcısı sistem
        // genelinde tek ayardan (admin paneli) yönetiliyor; kuruluş bazlı seçim
        // 2026-07-30'da kaldırıldı. Burada tutulması iki soruna yol açıyordu:
        // (1) alan ekranda hiç gösterilmediği hâlde kaydederken geri yazılıyor,
        // boş değeri "openai" olarak sabitleyip o kuruluşu sessizce OpenAI'a
        // kilitliyordu; (2) sağlayıcı adı müşteri panelinde hiçbir yerde
        // geçmemeli (CLAUDE.md UI politikası).
        tf_code_prefix: cur.tf_code_prefix,
        risk_file_code: cur.risk_file_code,
        answer_lang: cur.answer_lang,
      });
      // Sunucudaki dil tercihini yerel store'a uygula (müşteri bazlı).
      if (cur.answer_lang) selectAnswerLang(cur.answer_lang as never);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [selectAnswerLang]);

  useEffect(() => { void load(); }, [load]);

  const onLogoFile = useCallback((file: File) => {
    if (file.size > 800_000) {
      setErr(t("settings_logo_too_big"));
      return;
    }
    if (!/^image\/(png|jpe?g)$/.test(file.type)) {
      setErr(t("settings_logo_wrong_type"));
      return;
    }
    setErr(null);
    const reader = new FileReader();
    reader.onload = () => setDraft((d) => ({ ...d, logo_data_url: String(reader.result) }));
    reader.readAsDataURL(file);
  }, [t]);

  const onClearLogo = useCallback(() => setDraft((d) => ({ ...d, logo_data_url: "" })), []);

  const onSave = useCallback(async () => {
    setSaving(true);
    setErr(null);
    setOkMsg(null);
    try {
      // Dil tercihi de müşteri kaydına yazılır (server-side).
      const next = await updateSettings({ ...draft, answer_lang: answerLang });
      setS(next);
      setOkMsg(t("settings_save_success"));
      setTimeout(() => setOkMsg(null), 4000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, answerLang, t]);

  if (loading && !s) return <div className="settings"><p>{t("loading")}</p></div>;

  return (
    <div className="settings">
      <h2>{t("settings_title")}</h2>
      <p className="hint">{t("settings_intro")}</p>

      {err && <div className="error">{t("err_prefix")}: {err}</div>}
      {okMsg && <div className="success">{okMsg}</div>}

      <div className="settings-tabs">
        <button className={"settings-tab" + (tab === "general" ? " is-active" : "")} onClick={() => switchTab("general")}>
          🏢 {t("settings_tab_general")}
        </button>
        <button className={"settings-tab" + (tab === "credits" ? " is-active" : "")} onClick={() => switchTab("credits")}>
          <Icon name="coins" size={15} /> {t("man_tab_credits")}
        </button>
        <button className={"settings-tab" + (tab === "staff" ? " is-active" : "")} onClick={() => switchTab("staff")}>
          <Icon name="users" size={15} /> {t("man_tab_staff")}
        </button>
        <button className={"settings-tab" + (tab === "billing" ? " is-active" : "")} onClick={() => switchTab("billing")}>
          <Icon name="receipt" size={15} /> {t("man_tab_billing")}
        </button>
        <button className={"settings-tab" + (tab === "projects" ? " is-active" : "")} onClick={() => switchTab("projects")}>
          <Icon name="folder" size={15} /> {t("man_tab_projects")}
        </button>
        <button className={"settings-tab" + (tab === "language" ? " is-active" : "")} onClick={() => switchTab("language")}>
          🌐 {t("settings_tab_language")}
        </button>
      </div>

      {tab === "general" && <section className="settings-section">
        <h3>{t("settings_section_org")}</h3>
        <label className="form-row">
          <span>{t("settings_org_name")}</span>
          <input
            type="text"
            value={draft.org_name || ""}
            onChange={(e) => setDraft((d) => ({ ...d, org_name: e.target.value }))}
            placeholder={t("settings_org_placeholder")}
          />
        </label>

        <label className="form-row">
          <span title={t("settings_tf_prefix_title")}>{t("settings_tf_prefix_label")}</span>
          <input
            type="text"
            value={draft.tf_code_prefix || ""}
            onChange={(e) => setDraft((d) => ({ ...d, tf_code_prefix: e.target.value }))}
            placeholder={t("settings_tf_prefix_placeholder")}
          />
        </label>
        <small className="hint">{t("settings_tf_prefix_hint")}</small>

        <label className="form-row">
          <span title={t("settings_risk_file_title")}>{t("settings_risk_file_label")}</span>
          <input
            type="text"
            value={draft.risk_file_code || ""}
            onChange={(e) => setDraft((d) => ({ ...d, risk_file_code: e.target.value }))}
            placeholder={t("settings_risk_file_placeholder")}
          />
        </label>
        <small className="hint">{t("settings_risk_file_hint")}</small>

        <label className="form-row">
          <span>{t("settings_logo_label")}</span>
          <div className="logo-upload">
            {(draft.logo_data_url) && (
              <img src={draft.logo_data_url} alt="logo" className="logo-preview" />
            )}
            {/*
              Native dosya girdisi TARAYICI stiliyle çıkıyordu ("Choose File · No
              file chosen") ve panelin geri kalanıyla uyuşmuyordu. Projede zaten
              bir desen var — .file-picker: input görünmez ama tıklanabilir
              kalır (opacity:0, inset:0), üstünde kendi butonumuz görünür.
              Erişilebilirlik korunur: gerçek <input> hâlâ odaklanabilir ve
              klavyeyle açılır, bu yüzden aria-hidden VERİLMEZ.
            */}
            <span className="file-picker">
              <input
                type="file"
                accept="image/png,image/jpeg"
                aria-label={t("settings_logo_pick")}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onLogoFile(f);
                }}
              />
              <span className="file-picker-btn">
                <Icon name="upload" size={15} />
                {draft.logo_data_url ? t("settings_logo_replace") : t("settings_logo_pick")}
              </span>
            </span>
            {draft.logo_data_url && (
              <button type="button" className="btn-sec" onClick={onClearLogo}>{t("settings_logo_delete")}</button>
            )}
          </div>
        </label>
      </section>
      }

      {tab === "general" && <section className="settings-section">
        <h3>{t("settings_section_footer")}</h3>
        <p className="hint">{t("settings_footer_hint")}</p>
        <textarea
          value={draft.docx_footer || ""}
          onChange={(e) => setDraft((d) => ({ ...d, docx_footer: e.target.value }))}
          rows={3}
          placeholder={t("settings_footer_placeholder")}
          maxLength={1024}
        />
        <small className="hint">{(draft.docx_footer || "").length} / 1024</small>
      </section>
      }

      {tab === "credits" && <CreditsPanel />}
      {tab === "staff" && <StaffPanel />}
      {tab === "billing" && <BillingPanel />}
      {tab === "projects" && <ProjectsPanel />}

      {tab === "language" && <section className="settings-section">
        <h3>{t("settings_lang_section_title")}</h3>
        <p className="hint">{t("settings_lang_hint")}</p>
        <label className="form-row">
          <span>{t("lang_select_title")}</span>
          <LanguageSelect value={answerLang} onChange={selectAnswerLang} />
        </label>
        <small className="hint">{t("settings_lang_apply_note")}</small>
      </section>
      }

      {(tab === "general" || tab === "language") && (
        <div className="settings-actions">
          <button className="btn-pri" disabled={saving} onClick={onSave}>
            {saving ? t("saving") : t("settings_save_btn")}
          </button>
          <button className="btn-sec" disabled={saving} onClick={load}>
            {t("settings_reset")}
          </button>
        </div>
      )}
    </div>
  );
}
